import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { get } from 'es-toolkit/compat';
import { Form } from 'src/core/forms';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { artWorkEntryUrl } from 'src/components/CustomisationPage/utils/customisationsUrls';
import { push } from 'src/core/engine/router/index';
import { Button } from 'src/components/Button/Button';
import { CustomizationSkeleton } from 'src/elements/Skeletons/Skeletons';
import { useQuery } from '@apollo/client';
import {
    getCustomisationLocations,
    getCustomisationLocationsVariables,
} from 'src/queries/__generated__/getCustomisationLocations';
import getCustomisationLocationsQuery from 'src/queries/getCustomisationLocations.graphql';
import { AppendState as AppendCartState } from 'src/core/state/cart';
import { METHOD_PRINT_ID, EMBROIDERY_PRINT_ID } from 'src/constants/chooseLogo';
import Bugsnag from '@bugsnag/js';

import { MethodFieldset } from '../steps/MethodFieldSet';
import { Actions } from '../../Forms/Actions';
import { InfoBlock } from '../InfoBlock';
import { useCustomisation } from '../hooks/useCustomisation';
import {
    ApplicationMethod,
    ApplicationMethodAPI,
    Fields,
    methodFromLocations,
    Step,
} from '../feature/customisation.types';
import { AppendState, CustomisationMsg, EntryKind } from '../feature/customisation.actions';

import classes from './Pages.scss';

interface Props {
    itemIds: (string | number)[];
    applicationMethodFromLocations: ApplicationMethod;
    locationNames: string[];
    recommendedApplication?: ApplicationMethod;
}

export const Method: React.FC<Props> = (props) => {
    const dispatch = useDispatch();

    const customiseProduct = useCallback(() => {
        const path = artWorkEntryUrl({ itemIds: [Number(props.itemIds)!], kind: EntryKind.New });
        dispatch(push(path));
    }, [dispatch, props.itemIds]);

    function submitMethod(values) {
        const method =
            String(values[Fields.applicationMethod]) === String(2)
                ? ApplicationMethod.Print
                : ApplicationMethod.Embroidery;

        dispatch(CustomisationMsg('Customisation.MethodSubmit', { applicationMethod: method }));
    }

    const [applicationMethodId, setApplicationMethodId] = useState('');
    const applicationMethodName =
        applicationMethodId === METHOD_PRINT_ID
            ? 'Print'
            : applicationMethodId === EMBROIDERY_PRINT_ID
              ? 'Embroidery'
              : '';

    return (
        <div className={classes.fadein}>
            <h2 className={classes.header}>2. Choose application method</h2>
            <Form className={classes.form} onSubmit={submitMethod}>
                <MethodFieldset
                    applicationMethodFromLocations={props.applicationMethodFromLocations}
                    applicationMethodId={applicationMethodId}
                    setApplicationMethodId={setApplicationMethodId}
                    recommendedApplication={props.recommendedApplication}
                    className={classes.customBackground}
                />
                <div className={classes.applicationMethodAction}>
                    <Actions primaryText="Continue" direction="reverse" variant="space-between">
                        <Button variant="light" onClick={customiseProduct}>
                            Back a step
                        </Button>
                    </Actions>
                    <div className={classes.applicationMethodSelected}>
                        <span>{applicationMethodName} </span>selected
                    </div>
                </div>
            </Form>
            <InfoBlock />
        </div>
    );
};

const selector = (s: AppendState<AppendCartState>) => {
    const { item_ids } = s.customisation;
    const first_item_id = item_ids[0];
    const matchingItem = s.cart.items.find((item) => String(item.id) === String(first_item_id));
    if (!matchingItem) {
        throw new Error('Cannot recover. Matching basket item is not found');
    }
    return {
        summary: s.customisation.summary,
        firstSku: matchingItem.product.sku,
        applicationMethod: s.customisation.application_method,
    };
};

export const PageMethodConnected: React.FC = () => {
    const {
        loadStep,
        isPending,
        state: { application_method, availableLocations, item_ids, locationNames },
    } = useCustomisation();

    const { firstSku } = useSelector(selector, shallowEqual);

    const { loading, error, data } = useQuery<getCustomisationLocations, getCustomisationLocationsVariables>(
        getCustomisationLocationsQuery,
        {
            variables: { sku: firstSku },
            ssr: false,
        },
    );

    const apiEnumToLocalEnum = {
        [ApplicationMethodAPI.Print]: ApplicationMethod.Print,
        [ApplicationMethodAPI.Embroidery]: ApplicationMethod.Embroidery,
        [ApplicationMethodAPI.None]: ApplicationMethod.None,
    };
    const recommendedApplicationMethod =
        apiEnumToLocalEnum[get(data, `products.items[0].recommended_application`, ApplicationMethodAPI.None)];

    const selectedApplicationMethod = useMemo(() => {
        if (!availableLocations) {
            Bugsnag.notify('availableLocations was absent');
            return ApplicationMethod.Both;
        }
        return methodFromLocations(availableLocations, locationNames);
    }, [availableLocations, locationNames]);

    useEffect(() => {
        setTimeout(() => {
            loadStep({ step: Step.ApplicationMethod });
        });
    }, [loadStep]);

    if (isPending) return <CustomizationSkeleton />;

    return (
        <Method
            itemIds={item_ids}
            recommendedApplication={recommendedApplicationMethod}
            applicationMethodFromLocations={selectedApplicationMethod}
            locationNames={locationNames}
        />
    );
};
